import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CountryFlag } from "@/lib/identity";
import { LivePill } from "@/components/LivePill";
import { useLivePoll } from "@/lib/useLivePoll";
import {
  ArrowRight, Crown, ClockCounterClockwise, Medal,
} from "@phosphor-icons/react";

export default function RanksHub() {
  const { user } = useAuth();
  const [myProfile, setMyProfile] = useState(null);

  // Silent 30s poll of Top-5 Africa, with manual refresh + LIVE pill
  const { data: lbData, updatedAt, busy, refresh } = useLivePoll(
    async () => {
      const r = await api.get("/season/leaderboard", { params: { scope: "africa" } });
      return r.data;
    },
    { intervalMs: 30000 },
  );
  const top = (lbData?.entries || []).slice(0, 5);

  useEffect(() => {
    api.get("/season/profile/me").then((r) => setMyProfile(r.data)).catch(() => {});
  }, []);

  const myRank = myProfile?.season_rank;
  const mySeason = myProfile?.stats?.season_score ?? 0;

  return (
    <div className="space-y-7 max-w-5xl" data-testid="ranks-hub">
      {/* Hero */}
      <header>
        <p className="text-[11px] uppercase tracking-[0.18em] text-nk-gold/85 font-medium mb-2 flex items-center gap-2">
          <Crown size={13} weight="fill" />
          Standings
        </p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white">
          Where do you <span className="italic font-light text-nk-gold">stand?</span>
        </h1>
      </header>

      {/* Your rank ribbon */}
      <div className="nk-card p-4 sm:p-5 flex items-center gap-4" data-testid="ranks-my-row">
        <div className="shrink-0 w-12 h-12 rounded-md bg-nk-green/40 border border-nk-gold/40 flex items-center justify-center">
          <Crown size={22} weight={myRank && myRank <= 3 ? "fill" : "duotone"} className="text-nk-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.16em] text-white/45 font-medium">Your rank · season</p>
          <p className="font-display text-xl sm:text-2xl font-medium text-white tabular-nums mt-0.5">
            {myRank ? `#${myRank}` : "Unranked"}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[10px] uppercase tracking-[0.16em] text-white/45 font-medium">Season score</p>
          <p className="font-display text-xl sm:text-2xl font-medium text-nk-gold tabular-nums mt-0.5">
            {mySeason.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Top 5 live preview */}
      <section>
        <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
          <LivePill
            updatedAt={updatedAt}
            onRefresh={refresh}
            busy={busy}
            label="Live · Top 5 Africa"
            testid="ranks-live"
          />
          <Link to="/leaderboard" data-testid="ranks-tile-leaderboard"
                className="text-xs text-nk-gold inline-flex items-center gap-1 hover:gap-2 transition-all">
            Full board <ArrowRight size={11} />
          </Link>
        </div>
        <div className="nk-card divide-y divide-white/8">
          {top.length === 0 ? (
            <div className="p-5 text-center">
              <p className="text-sm text-white/55">No leaderboard data yet — be the first to play.</p>
            </div>
          ) : top.map((r, i) => (
            <div key={r.user_id || i}
                 className={`p-3.5 sm:p-4 flex items-center gap-3 ${user?.user_id === r.user_id ? "bg-nk-gold/5 border-l-2 border-nk-gold" : ""}`}
                 data-testid={`ranks-top-row-${i}`}>
              <span className={`font-display text-lg w-7 text-center shrink-0 tabular-nums ${
                i === 0 ? "text-nk-gold" : i === 1 ? "text-white" : i === 2 ? "text-nk-gold/70" : "text-white/45"
              }`}>{r.rank ?? i + 1}</span>
              {r.country && <CountryFlag country={r.country} size={14} />}
              <span className="flex-1 text-sm font-medium text-white truncate">{r.username}</span>
              <span className="font-mono text-sm text-nk-gold tabular-nums shrink-0">
                {(r.season_score ?? 0).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Other rank destinations */}
      <section>
        <p className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-medium mb-3">More</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SecondaryTile
            to="/history" Icon={ClockCounterClockwise}
            title="My History" body="Every game you've played."
            testid="ranks-tile-history"
          />
          <SecondaryTile
            to="/hall-of-fame" Icon={Medal}
            title="Hall of Fame" body="Past champions of every country."
            testid="ranks-tile-hof" gold
          />
        </div>
      </section>
    </div>
  );
}

function SecondaryTile({ to, Icon, title, body, testid, gold = false }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`nk-card nk-card-lift p-4 sm:p-5 group flex items-start gap-4 ${gold ? "border-nk-gold/30" : ""}`}
    >
      <div className={`shrink-0 w-11 h-11 rounded-md flex items-center justify-center ${
        gold ? "bg-nk-gold/15 group-hover:bg-nk-gold/30" : "bg-nk-green/35 group-hover:bg-nk-green/55"
      } transition-colors`}>
        <Icon size={20} weight={gold ? "fill" : "duotone"} className="text-nk-gold" />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`font-display text-base font-medium ${gold ? "text-nk-gold" : "text-white"}`}>
          {title}
        </p>
        <p className="text-xs text-white/55 mt-1 leading-relaxed">{body}</p>
        <p className={`text-xs mt-2 inline-flex items-center gap-1 group-hover:gap-2 transition-all ${
          gold ? "text-nk-gold" : "text-white/75"
        }`}>
          Open <ArrowRight size={11} weight="bold" />
        </p>
      </div>
    </Link>
  );
}
